import {
  buildFlatTreeItemsFromCorrelatedItems,
  useGetSavedCypherItemsQuery,
  useGetSavedCypherTreesQuery,
} from '@query/services/api';
import { useMemo } from 'react';

/**
 * Custom hook that encapsulates the logic to load a tree structure and saved cypher items from the API,
 * then constructing them into a flat array of saved cypher items for the presentation layer.
 *
 * @returns A flat array of saved cypher items.
 */
export const useCommonGetFlatSavedCypherTreeQuery = ({ skip }: { skip: boolean }) => {
  const {
    savedCypherTreeId,
    orderedItems,
    isLoading: isSavedCypherTreesQueryLoading,
    isSuccess: isSavedCypherTreesQuerySuccess,
    isError: isSavedCypherTreesQueryError,
    isUninitialized: isSavedCypherTreesQueryUninitialized,
  } = useGetSavedCypherTreesQuery(
    {},
    {
      skip,
      selectFromResult: (result) => {
        const [tree] = result.data ?? [];
        return {
          savedCypherTreeId: tree?.id ?? '',
          orderedItems: tree?.orderedItems ?? [],
          isLoading: result.isLoading,
          isSuccess: result.isSuccess,
          isError: result.isError,
          isUninitialized: result.isUninitialized,
        };
      },
    },
  );

  const {
    data: savedCypherTreeItems = [],
    isLoading: isSavedCypherTreeItemsQueryLoading,
    isSuccess: isSavedCypherTreeItemsQuerySuccess,
    isError: isSavedCypherTreeItemsQueryError,
    isUninitialized: isSavedCypherTreeItemsQueryUninitialized,
  } = useGetSavedCypherItemsQuery({ savedCypherTreeId }, { skip: skip || savedCypherTreeId === '' });

  const isLoading = isSavedCypherTreesQueryLoading || isSavedCypherTreeItemsQueryLoading;
  const isError = isSavedCypherTreesQueryError || isSavedCypherTreeItemsQueryError;

  // GetSavedCypherItemsQuery will be uninitialized if the savedCypherTreeId is not defined,
  // which will happen if there are no trees.
  // In this case, we consider the query successful if there are no items to load.
  const isSuccess =
    (isSavedCypherTreesQuerySuccess && isSavedCypherTreeItemsQuerySuccess) ||
    (!isSavedCypherTreesQueryUninitialized &&
      isSavedCypherTreesQuerySuccess &&
      isSavedCypherTreeItemsQueryUninitialized &&
      !isSavedCypherTreeItemsQuerySuccess);

  const flatSavedCypherTree = useMemo(() => {
    return buildFlatTreeItemsFromCorrelatedItems(orderedItems, savedCypherTreeItems);
  }, [orderedItems, savedCypherTreeItems]);

  return {
    savedCypherTreeId,
    flatSavedCypherTree,
    isLoading,
    isError,
    isSuccess,
  };
};
